/**
 * What a device keeps of its owner's history (g110), and the rules for
 * keeping it, as pure functions. The mirror stores these rows in `meta`, so
 * the database keeps its version; the store decides when to read and what to
 * show. "Reading history (g109)" in `docs/architecture-and-api.md` is the
 * contract this follows.
 *
 * - A kept series is valid while the stamp's `head` has not moved since it was
 *   read. When it moves, a first page is read down to the first action
 *   already held: older pages never shift, so nothing below is read again.
 * - When `epoch` moves, names and labels may have been changed or retired, so
 *   every kept row is dropped in the transaction that stores the new stamp. A
 *   retired name is never shown from a cache.
 * - Whether a list or an item is deleted now is judged from the device's own
 *   lists and items, never from a kept page.
 */

import type { ActionDetailView, ActionSummary, ActivityPage, ActivityStamp, CoverageView } from "../../api/src/shared/activity.js";

/** Whose history a page is: the account's, one list's or one item's. */
export type HistoryTarget =
  | { kind: "account" }
  | { kind: "list"; listId: string }
  | { kind: "item"; itemId: string };

export const STAMP_KEY = "activity";
/** Every kept row's key starts with this. U+FFFF closes the range. */
export const HISTORY_PREFIX = "activity:";
export const SERIES_PREFIX = "activity:series:";
export const DETAIL_PREFIX = "activity:detail:";

/** How much a device keeps, so history never crowds out the lists it is about. */
export const HISTORY_BOUNDS = {
  /** Kept series, the least recently used dropped first. The account's goes last. */
  series: 12,
  /** Actions a kept series holds on disk. Older pages read in a visit stay in memory. */
  actions: 300,
  /** Kept details, and the bytes they may take between them. */
  details: 30,
  detailBytes: 1024 * 1024,
} as const;

/** How many pages a head read follows before it gives up on meeting what it holds and starts again. */
export const HEAD_READ_PAGES = 5;

export function targetKey(target: HistoryTarget): string {
  return target.kind === "account" ? "account" : target.kind === "list" ? `list:${target.listId}` : `item:${target.itemId}`;
}

export function seriesKey(target: HistoryTarget): string {
  return `${SERIES_PREFIX}${targetKey(target)}`;
}

export function detailKey(actionId: string): string {
  return `${DETAIL_PREFIX}${actionId}`;
}

/** One series as kept: the pages read so far, newest first, and where they stop. */
export interface SeriesRow {
  v: 1;
  target: HistoryTarget;
  /** The stamp's `head` and `epoch` when this series was last brought up to date. */
  head: string | null;
  epoch: string;
  actions: ActionSummary[];
  /** The cursor to read older actions from. Null only once the series has reached the start. */
  next: string | null;
  /**
   * Where each page read ended, by its last action, with the cursor that
   * carries on from there. A cursor is the server's and cannot be made here,
   * so a series is only ever cut where a page ended.
   */
  marks: { after: string; next: string | null }[];
  coverage: CoverageView[] | null;
  /** When the server last answered for this series, by this device's clock. */
  readAt: number;
  lastUsed: number;
}

/** One action's detail as kept, with the changes read so far. */
export interface DetailRow {
  v: 1;
  actionId: string;
  /** The stamp's epoch it was read under: a detail names people too. */
  epoch: string;
  view: ActionDetailView;
  readAt: number;
  lastUsed: number;
  /** About what it costs to keep, in UTF-16 code units of its JSON. */
  bytes: number;
}

/** A query for a target's history, as the transport sends it. */
export function targetQuery(target: HistoryTarget): { list?: string; item?: string } {
  return target.kind === "list" ? { list: target.listId } : target.kind === "item" ? { item: target.itemId } : {};
}

/** A series begun from its first page. */
export function freshSeries(target: HistoryTarget, stamp: ActivityStamp, page: ActivityPage, now: number): SeriesRow {
  return {
    v: 1, target, head: stamp.head, epoch: stamp.epoch, actions: [...page.actions], next: page.next, marks: markOf(page.actions, page.next),
    coverage: page.coverage ? [...page.coverage] : null, readAt: now, lastUsed: now,
  };
}

function markOf(actions: readonly ActionSummary[], next: string | null): SeriesRow["marks"] {
  const last = actions[actions.length - 1];
  return last ? [{ after: last.id, next }] : [];
}

/**
 * Where a head read meets what a series holds: the actions newer than any it
 * holds, and whether the read reached one it holds. Pages are newest first and
 * never shift, so the first held action is where everything below is known.
 */
export function meet(held: readonly ActionSummary[], pages: readonly ActionSummary[]): { fresh: ActionSummary[]; met: boolean } {
  const ids = new Set(held.map((action) => action.id));
  const at = pages.findIndex((action) => ids.has(action.id));
  return at < 0 ? { fresh: [...pages], met: false } : { fresh: pages.slice(0, at), met: true };
}

/**
 * A series after a head read. Met, the new actions go on top and the rest is
 * kept. Not met within `HEAD_READ_PAGES`, the gap is not papered over: the
 * series starts again from what was read, with the cursor that continues it.
 */
export function advance(series: SeriesRow, stamp: ActivityStamp, pages: readonly ActionSummary[], met: boolean,
  marks: SeriesRow["marks"], coverage: readonly CoverageView[] | undefined, now: number): SeriesRow {
  const { fresh } = meet(series.actions, pages);
  const actions = met ? [...fresh, ...series.actions] : [...pages];
  return {
    ...series,
    head: stamp.head,
    epoch: stamp.epoch,
    actions,
    next: met ? series.next : marks[marks.length - 1]?.next ?? null,
    // A cursor names the action it follows, so every page end read is a place `bounded` may cut, the new ones included.
    marks: met ? inOrder(actions, [...marks, ...series.marks]) : [...marks],
    coverage: coverage ? [...coverage] : series.coverage,
    readAt: now,
    lastUsed: now,
  };
}

/** Page ends whose action a series holds, once each, in the series' order. */
function inOrder(actions: readonly ActionSummary[], marks: SeriesRow["marks"]): SeriesRow["marks"] {
  const index = new Map(actions.map((action, at) => [action.id, at]));
  const seen = new Set<string>();
  return marks
    .filter((mark) => index.has(mark.after) && !seen.has(mark.after) && Boolean(seen.add(mark.after)))
    .sort((a, b) => index.get(a.after)! - index.get(b.after)!);
}

/** An older page added below, skipping anything already held. */
export function extend(series: SeriesRow, page: ActivityPage, now: number): SeriesRow {
  const ids = new Set(series.actions.map((action) => action.id));
  const added = page.actions.filter((action) => !ids.has(action.id));
  const actions = [...series.actions, ...added];
  return { ...series, actions, next: page.next, marks: [...series.marks, ...markOf(actions, page.next)], readAt: now, lastUsed: now };
}

/**
 * A series as it goes to disk: cut at the last page end that keeps it within
 * `HISTORY_BOUNDS.actions`, so the cursor kept carries on from exactly the
 * last action kept. A series with no such end is kept whole.
 */
export function bounded(series: SeriesRow): SeriesRow {
  if (series.actions.length <= HISTORY_BOUNDS.actions) return series;
  const index = new Map(series.actions.map((action, at) => [action.id, at]));
  const fits = series.marks.filter((mark) => (index.get(mark.after) ?? Infinity) < HISTORY_BOUNDS.actions);
  const last = fits[fits.length - 1];
  if (!last) return series;
  return { ...series, actions: series.actions.slice(0, index.get(last.after)! + 1), next: last.next, marks: fits };
}

/** A summary replaced where a series holds it, as a re-read of an unfinished group gives it. */
export function replaced(series: SeriesRow, action: ActionSummary): SeriesRow {
  return { ...series, actions: series.actions.map((held) => (held.id === action.id ? action : held)) };
}

/** Whether a series was read under the stamp the device holds now. */
export function current(series: SeriesRow, stamp: ActivityStamp | null): boolean {
  return stamp !== null && series.head === stamp.head && series.epoch === stamp.epoch;
}

/** A value that looks like a stamp, as the one a reply may carry. Anything else is no stamp. */
export function readsStamp(value: unknown): value is ActivityStamp {
  if (typeof value !== "object" || value === null) return false;
  const stamp = value as Record<string, unknown>;
  return stamp.v === 1 && (stamp.head === null || typeof stamp.head === "string") && typeof stamp.epoch === "string";
}

export function detailBytes(view: ActionDetailView): number {
  return JSON.stringify(view).length;
}

/**
 * Which kept rows to drop so what is left fits the bounds: series by when
 * they were last used, the account's last, and details likewise, along with
 * any detail whose action no kept series holds.
 */
export function evictions(series: readonly SeriesRow[], details: readonly DetailRow[]): string[] {
  const drop: string[] = [];
  const bySeries = [...series].sort((a, b) => {
    const account = Number(b.target.kind === "account") - Number(a.target.kind === "account");
    return account !== 0 ? account : b.lastUsed - a.lastUsed;
  });
  const keptSeries = bySeries.slice(0, HISTORY_BOUNDS.series);
  for (const row of bySeries.slice(HISTORY_BOUNDS.series)) drop.push(seriesKey(row.target));
  const held = new Set(keptSeries.flatMap((row) => row.actions.map((action) => action.id)));
  let bytes = 0;
  let count = 0;
  for (const row of [...details].sort((a, b) => b.lastUsed - a.lastUsed)) {
    if (!held.has(row.actionId) || count >= HISTORY_BOUNDS.details || bytes + row.bytes > HISTORY_BOUNDS.detailBytes) {
      drop.push(detailKey(row.actionId));
      continue;
    }
    count += 1;
    bytes += row.bytes;
  }
  return drop;
}
